'use client';

/**
 * Shared tab pill row for any multi-tab page. Drives the URL via ?tab=<id>
 * (push state, no full reload). Tab labels + ids come from lib/data-surfaces.ts
 * so the actual page tabs match what /pipeline Projects shows.
 *
 * Usage:
 *   const [tab, setTab] = useUrlTab(PORTFOLIO_TABS, 'overview');
 *   <PageTabs surfaces={PORTFOLIO_TABS} active={tab} onChange={setTab} />
 */

import { useCallback, useEffect, useState } from 'react';
import { DataAsOf } from './DataAsOf';
import { UnreadDot, markViewedAuto } from './UnreadDot';
import type { Surface } from '@/lib/data-surfaces';

export function PageTabs({
  surfaces,
  active,
  onChange,
}: {
  surfaces: Surface[];
  active: string;
  /** Called for in-page tabs (no `route` override). Cross-route tabs navigate via window.location. */
  onChange: (id: string) => void;
}) {
  // Mark the active tab as viewed once on mount and whenever it changes —
  // a tab the user is currently looking at shouldn't sit there flashing.
  useEffect(() => {
    if (active) markViewedAuto(active);
  }, [active]);

  return (
    <div className="mb-10">
      <div className="flex items-center gap-1 border-b border-[#1e293b] flex-wrap">
        {surfaces.map((s) => {
          const isActive = active === s.id;
          const goesElsewhere = Boolean(s.route);
          // relative + bottom-aligned absolute DataAsOf so the badge hangs
          // directly below this tab without changing the tab pill's width.
          const className = `relative px-4 py-2 text-sm font-semibold tracking-wide uppercase border-b-2 -mb-px transition-colors whitespace-nowrap flex items-center gap-2 ${
            isActive
              ? 'border-amber-400 text-amber-300'
              : 'border-transparent text-[#94a3b8] hover:text-white hover:border-[#1e293b]'
          }`;
          const handleClick = () => {
            markViewedAuto(s.id);
            onChange(s.id);
          };
          const handleNavClick = () => {
            // Cross-route — mark viewed on the way out so the dot is solid by
            // the time we navigate back via the browser.
            markViewedAuto(s.id);
          };
          const inner = (
            <>
              {!isActive && <UnreadDot surfaceId={s.id} />}
              <span>{s.tabLabel}</span>
              {isActive && (
                <span className="absolute left-4 top-full mt-2 z-10 pointer-events-none normal-case tracking-normal">
                  <DataAsOf surfaceId={s.id} />
                </span>
              )}
            </>
          );
          return goesElsewhere ? (
            <a key={s.id} href={s.route} className={className} onClick={handleNavClick}>{inner}</a>
          ) : (
            <button key={s.id} type="button" onClick={handleClick} className={className}>{inner}</button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * SSR-safe URL-bound tab state.
 *
 * Initial render (SSR + first client render) always uses `fallback` so server
 * and client trees match. After mount, syncs to ?tab=<id> from the URL. When the
 * caller changes tab, replaces ?tab=<id> in the URL via history.replaceState.
 *
 * Why a hook (not just useState(() => readUrl()) like before): reading
 * window.location during state init returns different values on server vs client
 * for any non-default deep link, which trips React hydration. The mount-effect
 * pattern moves the URL read to after hydration, costing one frame of fallback
 * paint but eliminating the mismatch entirely.
 */
export function useUrlTab(
  surfaces: Surface[],
  fallback?: string,
): [string, (id: string) => void] {
  const initial = fallback ?? surfaces[0]?.id ?? '';
  const [tab, setTabState] = useState<string>(initial);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('tab');
    if (requested && surfaces.some((s) => s.id === requested) && requested !== tab) {
      setTabState(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTab = useCallback((id: string) => {
    setTabState(id);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', id);
    window.history.replaceState({}, '', url.toString());
  }, []);

  return [tab, setTab];
}
